import React from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiCheckCircle,
  FiUsers,
  FiGlobe,
  FiPackage,
  FiZap,
} from "react-icons/fi";
import "./About.css";

const ABOUT_HERO =
  "https://images.unsplash.com/photo-1524117074681-31bd4de22ad3?auto=format&fit=crop&w=1920&q=80";
const FACTORY =
  "https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=900&q=80";
const QUALITY =
  "https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&w=900&q=80";

const milestones = [
  {
    year: "2020",
    event: "Founded as a small-scale martial arts manufacturing unit.",
  },
  {
    year: "2021",
    event:
      "Scaled operations to include full in-house fabric knitting and dyeing capabilities.",
  },
  {
    year: "2022",
    event: "Began international exports to Europe and the Middle East.",
  },
  {
    year: "2023",
    event:
      "Expanded production capacity with new machinery and doubled our qualified staff.",
  },
  {
    year: "2024",
    event:
      "Rebranded to Shaheen Sports — serving clients worldwide with a dedicated team of specialists.",
  },
];

const values = [
  {
    icon: <FiCheckCircle size={24} />,
    title: "Uncompromising Quality",
    desc: "Every product passes multi-point inspection. We do not compromise on fabric integrity, stitching strength or finish quality.",
  },
  {
    icon: <FiUsers size={24} />,
    title: "Client-First Partnerships",
    desc: "We treat every client as a long-term partner. Your specification, timeline and branding requirements are our priority.",
  },
  {
    icon: <FiGlobe size={24} />,
    title: "Global Responsibility",
    desc: "We operate with ethical manufacturing standards, fair labor practices and sustainable material sourcing.",
  },
  {
    icon: <FiZap size={24} />,
    title: "Innovation Through R&D",
    desc: "Our research team continually develops new weave structures, GSM formulas and fabric blends to stay ahead of market demands.",
  },
];

export default function About() {
  return (
    <div className="about-page">
      {/* Hero */}
      <section className="page-hero about-page-hero">
        <div
          className="about-hero__img"
          style={{ backgroundImage: `url(${ABOUT_HERO})` }}
        />
        <div className="about-hero__overlay" />
        <div className="container page-hero__content">
          <span className="section-label">Our Story</span>
          <h1 className="page-hero__title">About Shaheen Sports</h1>
          <p className="page-hero__sub">
            From a small workshop in 2020 to a globally trusted martial arts
            manufacturer — this is who we are and how we got here.
          </p>
          <div className="about-hero__pills">
            <span className="about-hero__pill">
              <FiGlobe size={13} /> Worldwide
            </span>
            <span className="about-hero__pill">
              <FiUsers size={13} /> 50+ Staff
            </span>
            <span className="about-hero__pill">
              <FiPackage size={13} /> Full OEM
            </span>
            <span className="about-hero__pill">Est. 2020</span>
          </div>
        </div>
        <div className="about-hero__shape" />
      </section>

      {/* Story */}
      <section className="about-story">
        <div className="container about-story__inner">
          <div className="about-story__text">
            <span className="section-label">Who We Are</span>
            <h2 className="section-title">
              5 Years of Craftsmanship & Commitment
            </h2>
            <div className="divider-gold" />
            <p>
              Shaheen Sports is a vertically integrated manufacturer and
              exporter of professional martial arts uniforms, belts, MMA gear
              and sportswear, headquartered in Pakistan, Punjab, Pakistan.
            </p>
            <p style={{ marginTop: "1.2rem" }}>
              Our facility covers the complete production cycle — fabric
              weaving, knitting, yarn dyeing, computer-aided cutting, precision
              stitching, printing, quality inspection and export-standard
              packing.
            </p>
            <p style={{ marginTop: "1.2rem" }}>
              We specialize in Judo Gis, Jiujitsu Gis, MMA Shorts,
              Rash Guards, Spats, Sports Bras, Belts, Keychains and Custom
              Patches — serving clients worldwide.
            </p>
            <div className="about-story__highlights">
              {[
                "Worldwide",
                "50+ Qualified Staff",
                "100% In-House Production",
                "OEM / Private Label Available",
                "Est. 2020 — 5+ Years",
              ].map((h) => (
                <div key={h} className="about-story__highlight">
                  <FiCheckCircle size={15} color="var(--gold)" /> {h}
                </div>
              ))}
            </div>
          </div>

          <div className="about-story__sidebar">
            <div className="about-story__img-stack">
              <img
                src={FACTORY}
                alt="Our Manufacturing Facility"
                className="about-img-top"
                loading="lazy"
              />
              <img
                src={QUALITY}
                alt="Quality Control"
                className="about-img-bottom"
                loading="lazy"
              />
              <div className="about-story__overlay-badge">
                <div>
                  <strong>5+</strong>
                  <span>Years</span>
                </div>
                <div>
                  <strong>50+</strong>
                  <span>Staff</span>
                </div>
                <div>
                  <strong>🌍</strong>
                  <span>Worldwide</span>
                </div>
              </div>
            </div>
            <div className="about-info-cards">
              <div className="about-info-card">
                <strong>Founded</strong>
                <span>2020 — </span>
              </div>
              <div className="about-info-card">
                <strong>Specialization</strong>
                <span>Martial Arts Uniforms & Gear</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="about-values">
        <div className="container">
          <div className="about-values__header">
            <span className="section-label">Our Values</span>
            <h2 className="section-title">
              Principles That Guide Everything We Do
            </h2>
            <div className="divider-gold" />
          </div>
          <div className="values-grid">
            {values.map((v, i) => (
              <div key={i} className="value-card">
                <div className="value-card__icon">{v.icon}</div>
                <h3 className="value-card__title">{v.title}</h3>
                <p className="value-card__desc">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="timeline-section">
        <div className="container">
          <div className="timeline-section__header">
            <span className="section-label">Our Journey</span>
            <h2 className="section-title" style={{ color: "var(--white)" }}>
              Key Milestones
            </h2>
            <div className="divider-gold" />
          </div>
          <div className="timeline">
            {milestones.map((m, i) => (
              <div
                key={i}
                className={`timeline-item ${i % 2 === 0 ? "timeline-item--left" : "timeline-item--right"}`}
              >
                <div className="timeline-item__year">{m.year}</div>
                <div className="timeline-item__dot" />
                <div className="timeline-item__card">
                  <p>{m.event}</p>
                </div>
              </div>
            ))}
            <div className="timeline__line" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="about-cta">
        <div className="container about-cta__inner">
          <h2>Partner With Us</h2>
          <p>
            Whether you're an academy, retail brand or national federation — we
            have the capacity and expertise to fulfill your requirements.
          </p>
          <div className="about-cta__btns">
            <Link to="/contact" className="btn-primary">
              Get a Quote <FiArrowRight size={15} />
            </Link>
            <Link to="/all-products" className="btn-dark">
              Browse Products
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
